import { createSelector, createSlice } from '@reduxjs/toolkit';
import { sortItems } from '@utils/functions.js';

const initialState = {
  isLoading: false,
  isActive: false,
  id: 0,
  uid: 0,
  sid: 0,

  blockContent: {
    items: [],
    heading: {
      title: '',
      description: '',
      textAlign: 'left',
      image: '',
      divider: '',
      position: '',
    },
  },
  blockDesign: {
    postLayout: 0,
    layout: '2',
    view: 'feed',
  },
  blockSettings: {
    mode: 'products',
    buttonTitle: '',
    previewButton: '',
    isDisplayCategory: null,
    isDisplayItemAvailable: null,
    isDisplayItemCart: true,
    isDisplayItemCrossSale: null,
    isDisplayItemFavorite: null,
    isDisplaySearch: null,
    isDisplaySort: null,
    isUrgencyEnabled: null,
    urgencyType: null,
    urgencyCountdownMinutes: null,
    urgencyMessage: null,
    urgencyColor: null,
  },
  itemDesign: {},
  buttonDesign: {},
  containerDesign: {},
  categoryDesign: {},
  previewDesign: {},
  blockPreview: {
    isActive: false,
    view: 'compact',
    thumbnail: null,
    title: '',
    description: '',
    button: '',
    label: '',
    timeStart: null,
  },
};

export const shopSlice = createSlice({
  name: 'shop',
  initialState,
  reducers: {
    shopStore: (state, action) => {
      const { id, isActive, sid, uid, data, preview } = action.payload;

      const block = data ?? {};
      const { blockContent: rawBlockContent, blockDesign: rawBlockDesign, blockSettings: rawBlockSettings } = block;
      const blockContent = rawBlockContent ?? { items: [], heading: {} };
      const blockDesign = rawBlockDesign ?? {};
      const blockSettings = rawBlockSettings ?? {};
      const { itemDesign = {}, buttonDesign = {}, containerDesign, categoryDesign, previewDesign } = blockDesign;
      state.previewDesign = previewDesign ?? {};
      state.blockPreview = {
        isActive: preview?.isActive || false,
        thumbnail: preview?.thumbnail ?? null,
        view: preview?.view ?? 'compact',
        title: preview?.title ?? '',
        description: preview?.description ?? '',
        button: preview?.button ?? '',
        label: preview?.label ?? '',
        timeStart: preview?.timeStart ?? null,
      };

      state.isLoading = false;
      state.id = id ?? 0;
      state.isActive = isActive;
      state.sid = sid;
      state.uid = uid;

      state.itemDesign = {
        textColor: buttonDesign.textColor ?? '#ffffff',
        backgroundColor: buttonDesign.backgroundColor ?? '#000000',
        shape: itemDesign.shape ?? 'round',
        border: itemDesign.border ?? 'none',
        borderColor: itemDesign.borderColor ?? '#000000',
        shadow: itemDesign.shadow ?? 'none',
        shadowColor: itemDesign.shadowColor ?? '#000000',
        layout: itemDesign.layout ?? 'none',
      };
      state.buttonDesign = {
        textColor: buttonDesign.textColor ?? '#ffffff',
        backgroundColor: buttonDesign.backgroundColor ?? '#000000',
      };
      state.containerDesign = containerDesign ?? {};

      state.categoryDesign = categoryDesign ?? {
        itemDesign: {
          height: 'square',
          isFullWidth: false,
          layout: 'top',
          margin: 15,
          shape: 'medium',
          view: 'title',
        },
        titleDesign: {
          color: '#000000',
          fontFamily: 'Arial',
          fontSize: 14,
        },
        buttonDesign: {
          backgroundColor: '#ffffff',
          border: 'none',
          opacity: 0,
          shadow: 'none',
          textColor: '#000000',
        },
        view: categoryDesign?.view ?? 'feed',
      };

      const heading = blockContent.heading ?? {};
      state.blockContent = {
        items: blockContent.items ?? [],
        heading: {
          title: heading.title ?? '',
          description: heading.description ?? '',
          textAlign: heading.textAlign ?? 'left',
          image: heading.image ?? '',
          divider: heading.divider ?? '',
          position: heading.position ?? '',
        },
      };

      state.blockDesign = {
        layout: String(blockDesign.layout) ?? '2',
        view: blockDesign.view ?? 'feed',
        postLayout: blockDesign.postLayout ?? 0,
      };
      state.blockSettings = {
        mode: blockSettings.mode ?? 'products',
        buttonTitle: blockSettings.buttonTitle ?? '',
        previewButton: blockSettings.previewButton ?? '',
        isDisplayCategory: blockSettings.isDisplayCategory ?? null,
        isDisplayItemAvailable: blockSettings.isDisplayItemAvailable ?? null,
        isDisplayItemCart: blockSettings.isDisplayItemCart ?? true,
        isDisplayItemCrossSale: blockSettings.isDisplayItemCrossSale ?? null,
        isDisplayItemFavorite: blockSettings.isDisplayItemFavorite ?? null,
        isDisplaySearch: blockSettings.isDisplaySearch ?? null,
        isDisplaySort: blockSettings.isDisplaySort ?? null,
        isUrgencyEnabled: blockSettings.isUrgencyEnabled ?? null,
        urgencyType: blockSettings.urgencyType ?? null,
        urgencyCountdownMinutes: blockSettings.urgencyCountdownMinutes ?? null,
        urgencyMessage: blockSettings.urgencyMessage ?? null,
        urgencyColor: blockSettings.urgencyColor ?? null,
      };
    },
    shopClear: () => initialState,

    shopUpdateIsLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    shopUpdateIsActive: (state, action) => {
      state.isActive = action.payload;
    },
    shopUpdateBlockContent: (state, action) => {
      state.blockContent = action.payload;
    },
    shopUpdateBlockDesign: (state, action) => {
      state.blockDesign = action.payload;
    },
    shopUpdateBlockSettings: (state, action) => {
      state.blockSettings = action.payload;
    },

    shopUpdateCategoryDesign: (state, action) => {
      state.categoryDesign = action.payload;
    },
    shopUpdateBlockPreviewContent: (state, action) => {
      const { field, value } = action.payload;
      state.blockPreview[field] = value;
    },
    shopUpdateBlockPreviewDesign: (state, action) => {
      const { field, value } = action.payload;
      state.previewDesign[field] = value;
    },
  },
});

export const {
  shopStore,
  shopClear,
  shopUpdateIsLoading,
  shopUpdateIsActive,
  shopUpdateBlockContent,
  shopUpdateBlockDesign,
  shopUpdateBlockSettings,
  shopUpdateCategoryDesign,
  shopUpdateBlockPreviewContent,
  shopUpdateBlockPreviewDesign,
} = shopSlice.actions;

export const selectShopIsLoading = (state) => state.shop.isLoading;

export const selectShopId = (state) => state.shop.id;
export const selectShopUID = (state) => state.shop.uid;
export const selectShopSID = (state) => state.shop.sid;

export const selectShopBlockContent = (state) => state.shop.blockContent;
export const selectShopBlockDesign = (state) => state.shop.blockDesign;
export const selectShopSortItems = createSelector([selectShopBlockContent], ({ items }) => {
  return sortItems(items);
});

export const selectShopItemDesign = (state) => state.shop.itemDesign;
export const selectShopButtonDesign = (state) => state.shop.buttonDesign;
export const selectShopContainerDesign = (state) => state.shop.containerDesign;

export const selectShopCategoryDesign = (state) => state.shop.categoryDesign;

export const selectShopBlockSettings = (state) => state.shop.blockSettings;
export const selectShopPreviewContent = (state) => state.shop.blockPreview;
export const selectShopPreviewDesign = (state) => state.shop.previewDesign;

export default shopSlice.reducer;
